import type { GetUserProfileResponse } from '@otter-ai/recall-client';
import {
  type DefaultError,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  DISCONNECT_ERRORS,
  DISCONNECT_ERRORS_MESSAGES,
  type DisconnectErrors,
} from '@/constants/auth';
import { API_STATUS_FAILED } from '@/constants/error-codes';
import { legacyRestFetch } from '@/features/account-settings/legacyRestFetch';
import { userProfileQueryCreator } from '@/query/userProfileQueryCreator';
import { useGetCurrentUser } from '@/shared/hooks/current-user/useGetCurrentUser';
import {
  BUILT_IN_APPS,
  type BuiltInApps,
  type SyncedAccount,
} from '@/shared/types/apps';
import { getConnectedAppsQueryCreator } from '../query/getConnectedAppsQueryCreator';
import { teamAppsQueryCreator } from '../query/listTeamAppsQueryCreator';
import type { DropboxSyncedAccount } from '../type/apps';
import { useSyncedAccounts } from './useSyncedAccounts';

export type DisconnectAppVars = {
  service: BuiltInApps;
  account: SyncedAccount;
};

/**
 * Disconnect a synced account for the current user.
 *
 * @example
 * ```tsx
 * const Example() {
 *   const { disconnectAppAsync } = useDisconnectApp()
 *   const onClick = async () => {
 *     await disconnectAppAsync({ service: 'Microsoft' });
 *     addToast('You are no longer connected to Microsoft');
 *   }
 *
 *   return <button onClick={onClick}>Disconnect</button>
 * }
 * ```
 */
export function useDisconnectApp() {
  const { userId } = useGetCurrentUser();
  const { isLoading, getSyncedAccountsForService } = useSyncedAccounts();
  const queryClient = useQueryClient();

  const {
    isPending,
    mutate: disconnectApp,
    mutateAsync: disconnectAppAsync,
  } = useMutation<void, DefaultError, DisconnectAppVars>({
    mutationFn: async ({ service, account }: DisconnectAppVars) => {
      const connectedAccounts = getSyncedAccountsForService(service);
      if (connectedAccounts.length === 0) {
        throw new FailedDisconnect(DISCONNECT_ERRORS.NO_SYNCED_ACCOUNT);
      }

      if (!connectedAccounts.includes(account)) {
        throw new FailedDisconnect(DISCONNECT_ERRORS.NO_SYNCED_ACCOUNT);
      }

      switch (service) {
        case BUILT_IN_APPS.MICROSOFT:
          return deleteMicrosoftServerAuth(account, userId as number);
        case BUILT_IN_APPS.DROPBOX:
          return deleteDropboxServerAuth(account as DropboxSyncedAccount);
        case BUILT_IN_APPS.GOOGLE:
          return deleteGoogleServerAuth(account.account, userId as number);
        default:
          throw new Error('Unimplemented service');
      }
    },
    onSuccess: (_, payload) => {
      // Immediately remove the app so the UI is updated immediately
      queryClient.setQueryData(
        userProfileQueryCreator().queryKey,
        (record: GetUserProfileResponse) => {
          if (!record?.user?.synced_accounts) {
            throw new UnexpectedUserDataStructureError(
              'Expected `record.user.synced_accounts` to be defined,',
            );
          }
          const synced_accounts = (
            record.user.synced_accounts as { source: string; account: string }[]
          ).filter(({ source, account }) => {
            if (!payload?.account?.account) {
              console.error('payload.account.account is undefined', payload);
              return true;
            }
            return (
              source !== payload.service || payload.account.account !== account
            );
          });
          return { ...record, user: { ...record.user, synced_accounts } };
        },
      );
      return Promise.all([
        // Capture any other changes that may have happened besides `user.synced_accounts`.
        queryClient.invalidateQueries(userProfileQueryCreator()),
        queryClient.invalidateQueries(teamAppsQueryCreator()),
        queryClient.invalidateQueries(getConnectedAppsQueryCreator()),
        // TODO(sleahy): Use the constant `allMeetingsQueryKey` from https://github.com/otter-ai/otter-web/pull/4657
        queryClient.invalidateQueries({ queryKey: ['meetings'] }),
      ]);
    },
  });

  return {
    isLoading: !userId && isLoading,
    isPending,
    disconnectApp,
    disconnectAppAsync,
  };
}

async function deleteMicrosoftServerAuth(
  account: SyncedAccount,
  userId: number,
) {
  const body = new FormData();
  body.append('preferred_username', account.account);
  body.append('scope', account.permission.join(' '));

  // TODO(OTWEB-9058): Migrate delete_microsoft_server_auth to api-spec
  const response = await legacyRestFetch('delete_microsoft_server_auth', {
    method: 'POST',
    body,
    queryParams: { userid: userId.toString() },
  });
  if (!response.ok) {
    throw new FailedDisconnect(DISCONNECT_ERRORS.OPERATION_FAILED);
  }
  const result = await response.json();
  if (result.status === API_STATUS_FAILED) {
    throw new FailedDisconnect(DISCONNECT_ERRORS.OPERATION_FAILED, result);
  }
}

async function deleteDropboxServerAuth(account: DropboxSyncedAccount) {
  const body = new FormData();
  body.append('dropbox_account_id', account.dropbox_account_id);

  // TODO(OTWEB-9173): Migrate dropbox_disconnect to api-spec
  const response = await legacyRestFetch('dropbox_disconnect', {
    method: 'POST',
    body,
  });
  if (!response.ok) {
    throw new FailedDisconnect(DISCONNECT_ERRORS.OPERATION_FAILED);
  }
  const result = await response.json();
  if (result.status === API_STATUS_FAILED) {
    throw new FailedDisconnect(DISCONNECT_ERRORS.OPERATION_FAILED, result);
  }
}

async function deleteGoogleServerAuth(gmail: string, userId: number) {
  const body = new FormData();
  body.append('google_email', gmail);

  // TODO(OTWEB-9133): Migrate delete_google_server_auth to api-spec
  const response = await legacyRestFetch('delete_google_server_auth', {
    method: 'POST',
    body,
    queryParams: { userid: userId.toString() },
  });
  if (!response.ok) {
    throw new FailedDisconnect(DISCONNECT_ERRORS.OPERATION_FAILED);
  }
  const result = await response.json();
  if (result.status === API_STATUS_FAILED) {
    throw new FailedDisconnect(DISCONNECT_ERRORS.OPERATION_FAILED, result);
  }
}

export class FailedDisconnect extends Error {
  constructor(
    public disconnectError: DisconnectErrors,
    public source?: unknown,
  ) {
    super(DISCONNECT_ERRORS_MESSAGES[disconnectError]);
  }
}

export class UnexpectedUserDataStructureError extends Error {
  name = 'UnexpectedUserDataStructureError';
}
